import { z } from 'zod';
import { bankAccountSchema, bankTransferViewSchema } from './bank-transfer';
import { calendarDateSchema, paginatedSchema, paginationSchema, uuidSchema } from './common';
import { customerOrderShipmentSchema, orderShipmentSchema } from './order-shipments';
import { PAYMENT_STATUSES, paymentProviderSchema } from './payment';
import { destinationSizeSchema } from './quote-sizes';
import { issuedQuoteVersionSchema } from './quote-versions';
import { quoteRequiredTextSchema } from './quotes';
import {
  MAX_DESTINATIONS,
  memberDestinationInputSchema,
  orderDestinationSchema,
  shippingRegionSchema,
} from './shipping';
import { taxInvoiceViewSchema } from './tax-invoice';

export const ORDER_STATUSES = ['PENDING_PAYMENT', 'PAID', 'CANCELED', 'REFUNDED'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];
export const ORDER_FULFILLMENT_STATUSES = [
  'WAITING',
  'IN_PRODUCTION',
  'SHIPPED',
  'DELIVERED',
] as const;
export type OrderFulfillmentStatus = (typeof ORDER_FULFILLMENT_STATUSES)[number];
export const ORDER_CANCEL_REQUEST_KINDS = ['CUSTOMER', 'SUPPLY_FAILURE'] as const;
export type OrderCancelRequestKind = (typeof ORDER_CANCEL_REQUEST_KINDS)[number];
export const ORDER_CANCEL_REQUEST_STATUSES = ['REQUESTED', 'ACCEPTED', 'REJECTED'] as const;
export type OrderCancelRequestStatus = (typeof ORDER_CANCEL_REQUEST_STATUSES)[number];
/** 시스템이 주문을 취소한 사유 코드(`orders.cancel_reason`) — 입금 기한 초과 자동 취소. */
export const ORDER_CANCEL_REASONS = ['DEPOSIT_OVERDUE'] as const;
export type OrderCancelReason = (typeof ORDER_CANCEL_REASONS)[number];
export const MAX_ORDER_REASON_LENGTH = 500;
export const orderReasonSchema = z
  .string()
  .trim()
  .min(1, '사유를 입력해 주세요.')
  .max(MAX_ORDER_REASON_LENGTH);

/** 형식뿐 아니라 윤년·월별 일수도 검사한다. 현재 시각에 따른 범위는 도메인이 검사한다. */
export const orderDateSchema = calendarDateSchema;

/**
 * 주문 배송지 주소(주문하기 사본·배송지 변경) — 견적 회원 배송지 입력에서 수량만 뺀 계약(strict·칸별 상한). 우편번호는 5자리 숫자라
 * 지역 판별이 입력 단계에서 실패하지 않는다(일반 지역으로 떨어지지 않는다, SUPER-86 spec §9 Review Focus 3).
 */
export const orderDestinationAddressSchema = memberDestinationInputSchema.omit({
  quantity: true,
  sizes: true,
});
export type OrderDestinationAddress = z.infer<typeof orderDestinationAddressSchema>;
/** 주문하기 배송지 한 곳 — 견적 배송지 id + 주소(평평한 모양, 교차 조정 R8). 수량은 견적 배송지에서 복사한다(입력 없음). */
export const placeOrderDestinationInputSchema = orderDestinationAddressSchema.extend({
  quoteDestinationId: uuidSchema.optional(),
});
export type PlaceOrderDestinationInput = z.infer<typeof placeOrderDestinationInputSchema>;
export const placeOrderInputSchema = z.object({
  quoteRequestId: uuidSchema,
  versionId: uuidSchema,
  termsAgreed: z.literal(true),
  withdrawalNoticeAgreed: z.literal(true),
  destinations: z.array(placeOrderDestinationInputSchema).min(1).max(MAX_DESTINATIONS),
});
export type PlaceOrderInput = z.infer<typeof placeOrderInputSchema>;
export const getOrderInputSchema = z.object({ orderId: uuidSchema });
export type GetOrderInput = z.infer<typeof getOrderInputSchema>;
export const cancelUnpaidOrderInputSchema = getOrderInputSchema;
export type CancelUnpaidOrderInput = z.infer<typeof cancelUnpaidOrderInputSchema>;
export const listMyOrdersInputSchema = paginationSchema;
export type ListMyOrdersInput = z.infer<typeof listMyOrdersInputSchema>;
export const requestOrderCancelInputSchema = getOrderInputSchema.extend({
  reason: orderReasonSchema,
  /** 계좌이체 결제 주문의 환불 받을 계좌 — 토스 결제 주문은 보내지 않는다. */
  refundAccount: bankAccountSchema.optional(),
});
export type RequestOrderCancelInput = z.infer<typeof requestOrderCancelInputSchema>;
/** 주문 배송지 하나의 주소 변경(SUPER-86 §5-4) — 어느 배송지인지(`destinationId`)와 5자리 우편번호 주소. */
export const updateOrderAddressInputSchema = getOrderInputSchema.extend({
  destinationId: uuidSchema,
  shippingAddress: orderDestinationAddressSchema,
});
export type UpdateOrderAddressInput = z.infer<typeof updateOrderAddressInputSchema>;
/**
 * 관리자 주문 목록 필터. TO_PROCESS(처리할 건)는 운영자가 손댈 주문 — 결제 뒤 배송 완료 전 + 입금 확인을 기다리는 계좌이체.
 * IN_FULFILLMENT 는 결제 뒤 배송 완료 전만 — 제작 발주 자료 화면의 고정 필터라 상태 선택칸에는 없다.
 */
export const ORDER_ADMIN_FILTERS = [
  'TO_PROCESS',
  'IN_FULFILLMENT',
  'PENDING_PAYMENT',
  'DELIVERED',
  'REFUNDED',
  'CANCELED',
  'ALL',
] as const;
export type OrderAdminFilter = (typeof ORDER_ADMIN_FILTERS)[number];
/** 관리자 목록 검색어 상한 — 다른 목록 검색(회원·상품)과 같은 100자. admin 검색 입력의 maxLength 와 맞춘다. */
export const MAX_ORDER_ADMIN_SEARCH_LENGTH = 100;
export const listOrdersAdminInputSchema = paginationSchema.extend({
  filter: z.enum(ORDER_ADMIN_FILTERS).default('TO_PROCESS'),
  search: z.string().trim().max(MAX_ORDER_ADMIN_SEARCH_LENGTH).optional(),
});
export type ListOrdersAdminInput = z.infer<typeof listOrdersAdminInputSchema>;
export const startProductionInputSchema = getOrderInputSchema.extend({
  expectedShipDate: orderDateSchema,
});
export type StartProductionInput = z.infer<typeof startProductionInputSchema>;
export const notifyOrderDelayInputSchema = getOrderInputSchema.extend({
  newDate: orderDateSchema,
  reason: orderReasonSchema,
});
export type NotifyOrderDelayInput = z.infer<typeof notifyOrderDelayInputSchema>;
export const markOrderDeliveredInputSchema = getOrderInputSchema.extend({
  deliveredOn: orderDateSchema,
});
export type MarkOrderDeliveredInput = z.infer<typeof markOrderDeliveredInputSchema>;
export const refundOrderCancelInputSchema = getOrderInputSchema.extend({
  reason: orderReasonSchema.optional(),
});
export type RefundOrderCancelInput = z.infer<typeof refundOrderCancelInputSchema>;
export const rejectOrderCancelInputSchema = z.object({
  requestId: uuidSchema,
  reason: orderReasonSchema,
});
export type RejectOrderCancelInput = z.infer<typeof rejectOrderCancelInputSchema>;
export const cancelForSupplyFailureInputSchema = requestOrderCancelInputSchema;
export type CancelForSupplyFailureInput = z.infer<typeof cancelForSupplyFailureInputSchema>;

export const ORDER_BADGES = [
  'CANCEL_REQUESTED',
  'DELAYED',
  'ADDRESS_UPDATED',
  'WITHDRAWN_USER',
  'TRACKING_EXPIRED',
  'RETURNED',
  'TRACKING_REGISTER_FAILED',
  /** 관리자만(고객 상세는 배지 칸이 없고 고객 목록은 `OrderReadService.listMine` 이 뺀다): 배송 중인데 송장이 없는 배송지가 있음(spec §5-5). */
  'DESTINATION_NOT_SHIPPED',
] as const;
export type OrderBadge = (typeof ORDER_BADGES)[number];
const wonSchema = z.number().int().nonnegative();
const timestampSchema = z.iso.datetime();
/**
 * 행위자 표시 이름 — 운영자 uuid 옆에 싣는 사람 이름. 탈퇴한 계정은 '탈퇴한 계정', 회원 행이 없으면(FK 라 없어야 한다) '알 수 없음'.
 * 고객 투영(`customerOrderDetailSchema`·`customerOrderShipmentSchema`)은 uuid 와 함께 뺀다.
 */
const actorNameSchema = z.string();
/**
 * 관리자 주문 배송지 — 기본형(`shipping.ts` 의 `orderDestinationSchema`: 사본·수량·지역|null·견적 배송지 id·변경 시각)에 변경자 uuid·
 * 표시 이름과 그 배송지의 송장(추적 내부 칸 포함)을 더한다. 고객 투영이 파싱으로 칸을 지우도록 strict 가 아닌 z.object 다
 * (`customerOrderDetailSchema` 와 같은 이유). `order-shipments.ts` 옆에 두어 `shipping.ts` 가 송장 계약을 import 하지 않게 한다.
 */
export const adminOrderDestinationSchema = z.object({
  ...orderDestinationSchema.shape,
  addressUpdatedBy: uuidSchema.nullable(),
  addressUpdatedByName: actorNameSchema.nullable(),
  shipments: z.array(orderShipmentSchema),
});
export type AdminOrderDestination = z.infer<typeof adminOrderDestinationSchema>;
/** 고객 주문 배송지 — 변경자 uuid·표시 이름을 빼고 송장은 고객 투영으로. */
export const customerOrderDestinationSchema = z.object({
  ...orderDestinationSchema.shape,
  shipments: z.array(customerOrderShipmentSchema),
});
export type CustomerOrderDestination = z.infer<typeof customerOrderDestinationSchema>;
export const orderSummarySchema = z.object({
  id: uuidSchema,
  orderNumber: z.string(),
  quoteRequestId: uuidSchema,
  quoteNumber: z.string(),
  userId: uuidSchema,
  createdAt: timestampSchema,
  paidAt: timestampSchema.nullable(),
  productName: z.string(),
  quantity: z.number().int().positive(),
  amount: wonSchema.positive(),
  status: z.enum(ORDER_STATUSES),
  fulfillmentStatus: z.enum(ORDER_FULFILLMENT_STATUSES),
  displayStatus: z.string(),
  expectedShipDate: orderDateSchema.nullable(),
  badges: z.array(z.enum(ORDER_BADGES)),
  companyName: z.string().nullable(),
  contactName: z.string(),
  pendingCancelRequest: z
    .object({
      kind: z.enum(ORDER_CANCEL_REQUEST_KINDS),
      requestedAt: timestampSchema,
      refundDueDate: orderDateSchema,
    })
    .nullable(),
  /**
   * 견적 사본 썸네일 표시 주소(견적 디자인 미리보기 설계 D9·D10) — 견적의 `thumbnail_key` 를 조회 때마다 서명한다(만료가 있어
   * 저장하지 않는다). worker 가 아직 그리지 않았거나 그릴 수 없는 견적이면 null. 상세는 요약을 물려받아 같은 칸을 싣는다.
   */
  thumbnailUrl: z.string().nullable(),
  /** 결제 수단 — 결제 시도가 없으면 null. */
  paymentProvider: paymentProviderSchema.nullable(),
  /** 계좌이체 입금 기한 — 계좌이체 입금 대기 중일 때만 값이 있다. */
  depositDueAt: timestampSchema.nullable(),
});
export type OrderSummary = z.infer<typeof orderSummarySchema>;
export const orderListSchema = paginatedSchema(orderSummarySchema);
export type OrderList = z.infer<typeof orderListSchema>;

export const orderDelayNoticeSchema = z.object({
  id: uuidSchema,
  previousDate: orderDateSchema.nullable(),
  newDate: orderDateSchema,
  reason: orderReasonSchema,
  createdBy: uuidSchema,
  createdByName: actorNameSchema,
  createdAt: timestampSchema,
});
export type OrderDelayNotice = z.infer<typeof orderDelayNoticeSchema>;
export const orderCancelRequestSchema = z.object({
  id: uuidSchema,
  kind: z.enum(ORDER_CANCEL_REQUEST_KINDS),
  status: z.enum(ORDER_CANCEL_REQUEST_STATUSES),
  reason: orderReasonSchema,
  requestedBy: uuidSchema,
  requestedByName: actorNameSchema,
  requestedAt: timestampSchema,
  refundDueDate: orderDateSchema,
  resolvedAt: timestampSchema.nullable(),
  resolvedBy: uuidSchema.nullable(),
  resolvedByName: actorNameSchema.nullable(),
  rejectReason: orderReasonSchema.nullable(),
});
export type OrderCancelRequest = z.infer<typeof orderCancelRequestSchema>;

export const orderPaymentSummarySchema = z.object({
  id: uuidSchema,
  provider: paymentProviderSchema,
  status: z.enum(PAYMENT_STATUSES),
  amount: wonSchema.positive(),
  balanceAmount: wonSchema,
  method: z.string().nullable(),
  approvedAt: timestampSchema.nullable(),
  receiptUrl: z.string().nullable(),
});
export type OrderPaymentSummary = z.infer<typeof orderPaymentSummarySchema>;

export const orderDetailSchema = orderSummarySchema.extend({
  productionStartedAt: timestampSchema.nullable(),
  productionStartedBy: uuidSchema.nullable(),
  productionStartedByName: actorNameSchema.nullable(),
  shippedAt: timestampSchema.nullable(),
  deliveredOn: orderDateSchema.nullable(),
  deliveryCompletedBy: uuidSchema.nullable(),
  deliveryCompletedByName: actorNameSchema.nullable(),
  canceledAt: timestampSchema.nullable(),
  refundedAt: timestampSchema.nullable(),
  /** 주문 배송지(sortOrder 순, 1곳 이상) — 배송지마다 그 배송지의 송장을 담는다. */
  destinations: z.array(adminOrderDestinationSchema).min(1),
  delayNotices: z.array(orderDelayNoticeSchema),
  cancelRequests: z.array(orderCancelRequestSchema),
  customerEmail: z.email().nullable(),
  /**
   * 주문한 발행본 — 견적이 소유한 발행본 계약(`issuedQuoteVersionSchema`, 공급자·수신자 사본 포함)을 그대로 쓴다.
   * 모든 상태의 상세가 견적서 인쇄를 두므로(주문 spec §8-2, 견적 spec §7-10) 투영을 따로 만들지 않는다.
   */
  quoteVersion: issuedQuoteVersionSchema,
  payment: orderPaymentSummarySchema.nullable(),
  /** 계좌이체 진행 정보 — 토스 결제나 결제 전이면 null. */
  bankTransfer: bankTransferViewSchema.nullable(),
  /** 세금계산서 상태 — 요청이 없으면 status NONE. 운영자 상세 기록은 `taxInvoiceManagement.get`. */
  taxInvoice: taxInvoiceViewSchema,
});
export type OrderDetail = z.infer<typeof orderDetailSchema>;

/**
 * 고객 주문 상세(회원 경로 `orders.getMine`·`cancel`·`requestCancel`·`updateShippingAddress` 응답) — 관리자 상세에서 운영자 uuid·
 * 표시 이름(제작 착수·배송 완료·배송지 변경·송장 등록·지연 안내 작성·취소 요청 요청자/처리자), 송장의 추적 내부 값, 관리자 목록 배지를
 * 뺀다. 주문 spec §8-2 "내 주문 상세" 표시 항목만 남긴다. 파싱하면 계약 밖 칸을 지우므로 관리자 상세를 넘겨도 고객 상세가 된다.
 */
export const customerOrderDetailSchema = orderDetailSchema
  .omit({
    badges: true,
    productionStartedBy: true,
    productionStartedByName: true,
    deliveryCompletedBy: true,
    deliveryCompletedByName: true,
  })
  .extend({
    destinations: z.array(customerOrderDestinationSchema).min(1),
    delayNotices: z.array(orderDelayNoticeSchema.omit({ createdBy: true, createdByName: true })),
    cancelRequests: z.array(
      orderCancelRequestSchema.omit({
        requestedBy: true,
        requestedByName: true,
        resolvedBy: true,
        resolvedByName: true,
      }),
    ),
  });
export type CustomerOrderDetail = z.infer<typeof customerOrderDetailSchema>;

/**
 * 관리자 환불 결과(`orderManagement.refundCancel`·`cancelForSupplyFailure`) — 환불이 끝났으면 관리자 상세, 토스 결과를 모르면
 * 정해진 안내만(주문 spec §6-2 — 결제 상세의 다시 시도로 마무리). api 라우터 출력과 admin E2E fixture 가 같이 쓴다.
 */
export const orderRefundResultSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('COMPLETED'), order: orderDetailSchema }),
  z.object({ kind: z.literal('UNKNOWN'), message: z.literal('환불 확인 중') }),
  /** 계좌이체 결제 — 운영자가 직접 송금한 뒤 환불 완료를 기록한다. */
  z.object({ kind: z.literal('MANUAL_PENDING') }),
]);
export type OrderRefundResult = z.infer<typeof orderRefundResultSchema>;

export const getOrderPlacementInputSchema = z.object({ quoteRequestId: uuidSchema });
export type GetOrderPlacementInput = z.infer<typeof getOrderPlacementInputSchema>;
/** 주문 제작 상품 청약철회 제한 별첨 고지(약관 초안 별첨, 제15조③) — 원문과 버전. 원천은 견적 발행 설정의 공통 문구다. */
export const orderWithdrawalNoticeSchema = z.strictObject({
  text: quoteRequiredTextSchema,
  version: quoteRequiredTextSchema,
});
export type OrderWithdrawalNotice = z.infer<typeof orderWithdrawalNoticeSchema>;
/**
 * 주문하기 배송지 한 곳(SUPER-86 spec §5-3) — 견적 배송지 순서·수량·지역·발행본 배송비 줄 단가. 지역은 우편번호로 매번 구하고 이행한
 * 옛 행의 우편번호가 5자리 숫자가 아니면 null(교차 조정 R1 — 지역 제한 없음), 그 배송지의 배송비 줄이 없는 발행본이면 배송비 null.
 * 비회원 출처는 받는 분·연락처·상세주소가 null 이라 고객이 채운다.
 */
export const orderPlacementDestinationSchema = z.strictObject({
  quoteDestinationId: uuidSchema,
  sortOrder: z.number().int().nonnegative(),
  quantity: z.number().int().positive(),
  sizes: z.array(destinationSizeSchema),
  region: shippingRegionSchema.nullable(),
  shippingFee: wonSchema.nullable(),
  address: z.strictObject({
    recipientName: z.string().nullable(),
    recipientPhone: z.string().nullable(),
    postalCode: z.string(),
    address1: z.string(),
    address2: z.string().nullable(),
  }),
});
export type OrderPlacementDestination = z.infer<typeof orderPlacementDestinationSchema>;
/**
 * 주문하기 화면 데이터(R17, `orders.getPlacement`) — 화면에 보일 최신 발행본(공통 문구 `termsText`·`termsVersion` 포함),
 * 견적 요청 배송지 목록(배송지가 없는 견적은 빈 목록 — 주문할 수 없다, 교차 조정 R15), 별첨 고지 원문·버전.
 * 주문하기 동의 기록의 `terms_version` 은 이 발행본의 `termsVersion`, `withdrawal_notice_version` 은 이 고지의 `version` 이다.
 * 발행 설정이 없으면 화면을 열지 않는다(fail-closed).
 */
export const orderPlacementSchema = z.strictObject({
  version: issuedQuoteVersionSchema,
  destinations: z.array(orderPlacementDestinationSchema).max(MAX_DESTINATIONS),
  withdrawalNotice: orderWithdrawalNoticeSchema,
  /** 견적 사본 썸네일 표시 주소(설계 D9) — 아직 없으면 null. 화면 그림은 주문하기 화면 디자인 작업에서 붙인다. */
  thumbnailUrl: z.string().nullable(),
});
export type OrderPlacement = z.infer<typeof orderPlacementSchema>;
